import type { Tier, TierId, BillingPeriod } from "@mw/shared/pricing";
import { getAgentIncludedMinutes } from "@mw/shared/pricing";

// Re-export shared value types and constructors
export type { FormattedValue, CurrencyUnit } from "./pricing-value-types";
export { numberValue, currencyValue, minutesValue, textValue, booleanValue } from "./pricing-value-types";
import type { FormattedValue } from "./pricing-value-types";
import { numberValue, currencyValue, minutesValue, textValue, booleanValue } from "./pricing-value-types";

export type { BillingPeriod } from "@mw/shared/pricing";

export type AgentsPricingSection = "conversational_ai";

// Translation key types matching en.json structure
export type AgentsPricingRowTranslationKey =
  | "calls_per_minute"
  | "text_message_cost"
  | "llm_tokens"
  | "telephony_provider"
  | "minutes_included"
  | "additional_minutes"
  | "burst_pricing"
  | "concurrency_limit"
  | "tts"
  | "stt"
  | "knowledge_bases"
  | "rag"
  | "telephony";

export type AgentsPricingSubSectionTranslationKey =
  | "hosting_cost"
  | "model_provider_cost"
  | "usage_scale"
  | "included_features";

export interface AgentsPricingRow {
  labelKey: AgentsPricingRowTranslationKey;
  getValue: (tier: Tier, period: BillingPeriod) => FormattedValue;
  /** Optional tooltip translation key (e.g., "burst_pricing_tooltip") */
  tooltipKey?: AgentsPricingRowTranslationKey | `${AgentsPricingRowTranslationKey}_tooltip`;
}

export interface AgentsPricingSectionConfig {
  subSections: AgentsPricingSubSection[];
}

export interface AgentsPricingSubSection {
  labelKey?: AgentsPricingSubSectionTranslationKey;
  rows: AgentsPricingRow[];
}

// Helper to get hosting cost per minute
export const getHostingCostPerMinute = (tier: Tier): number | undefined => {
  return tier.platformConfig.agents.audioUsdPerMinute;
};

const getExtraMinuteCost = (tier: Tier): number | undefined => {
  return tier.platformConfig.agents.audioBurstUsdPerMinute ?? undefined;
};

// Precision for agents pricing currency values (small numbers need more decimals)
const AGENTS_CURRENCY_PRECISION = 3;

// Hosting Cost rows
const HOSTING_COST_ROWS: AgentsPricingRow[] = [
  {
    labelKey: "calls_per_minute",
    getValue: tier => currencyValue(getHostingCostPerMinute(tier), "flat", AGENTS_CURRENCY_PRECISION),
  },
  {
    labelKey: "text_message_cost",
    getValue: tier =>
      currencyValue(
        tier.platformConfig.agents.textMessageUsdPerMessage ?? undefined,
        "flat",
        AGENTS_CURRENCY_PRECISION,
      ),
  },
];

// Model Provider Cost rows
const MODEL_PROVIDER_ROWS: AgentsPricingRow[] = [
  {
    labelKey: "llm_tokens",
    getValue: () => textValue("based_on_usage"),
  },
  {
    labelKey: "telephony_provider",
    getValue: () => textValue("at_cost"),
  },
];

// Usage & Scale rows
const USAGE_SCALE_ROWS: AgentsPricingRow[] = [
  {
    labelKey: "minutes_included",
    getValue: tier => minutesValue(getAgentIncludedMinutes(tier)),
  },
  {
    labelKey: "additional_minutes",
    getValue: tier => currencyValue(getExtraMinuteCost(tier), "flat", AGENTS_CURRENCY_PRECISION),
  },
  {
    labelKey: "burst_pricing",
    tooltipKey: "burst_pricing_tooltip",
    getValue: tier => {
      const base = tier.platformConfig.agents.audioBurstUsdPerMinute;
      if (base == null) return { type: "unavailable" };
      return currencyValue(base * 2, "flat", AGENTS_CURRENCY_PRECISION);
    },
  },
  {
    labelKey: "concurrency_limit",
    tooltipKey: "concurrency_limit_tooltip",
    getValue: tier => numberValue(tier.platformConfig.agents.concurrencyLimit),
  },
];

// Included Features rows - all tiers get checkmarks for these features
const INCLUDED_FEATURES_ROWS: AgentsPricingRow[] = [
  { labelKey: "tts", getValue: () => booleanValue(true) },
  { labelKey: "stt", getValue: () => booleanValue(true) },
  { labelKey: "knowledge_bases", getValue: () => booleanValue(true) },
  { labelKey: "rag", getValue: () => booleanValue(true) },
  { labelKey: "telephony", getValue: () => booleanValue(true) },
];

// Section configuration - single section with three subsections
export const AGENTS_PRICING_SECTIONS: Record<AgentsPricingSection, AgentsPricingSectionConfig> = {
  conversational_ai: {
    subSections: [
      {
        labelKey: "hosting_cost",
        rows: HOSTING_COST_ROWS,
      },
      {
        labelKey: "model_provider_cost",
        rows: MODEL_PROVIDER_ROWS,
      },
      {
        labelKey: "usage_scale",
        rows: USAGE_SCALE_ROWS,
      },
      {
        labelKey: "included_features",
        rows: INCLUDED_FEATURES_ROWS,
      },
    ],
  },
};

// Default tiers for agents pricing (excludes enterprise)
export const DEFAULT_AGENTS_PRICING_TIERS: TierId[] = [
  "payg",
  "starter",
  "creator",
  "pro",
  "scale",
  "scale_2024_08_10",
];

// Section display order
export const AGENTS_PRICING_SECTION_ORDER: AgentsPricingSection[] = ["conversational_ai"];
